import { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, SquareMinus, Download } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { useDownloadModalStore } from '@/stores/download-manager';
import { cn } from '@/lib/utils';

export const ModalDownloadManager = () => {
  const {
    isOpen,
    mode,
    totalData,
    limitPerFile,
    progress,
    totalBatches,
    isLoading,
    date,
    title,
    onConfirm,
    onCancel,
    closeModal,
  } = useDownloadModalStore();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const percent = totalBatches > 0 ? Math.round((progress / totalBatches) * 100) : 0;
  const totalBatchCalc = Math.ceil(totalData / limitPerFile);

  if (isMinimized)
    return (
      <div
        onClick={() => setIsMinimized(false)}
        className={cn(
          'max-w fixed right-5 bottom-5 z-50 cursor-pointer rounded-lg border border-gray-300 bg-white shadow-md transition hover:shadow-lg',
        )}
      >
        <div className="flex items-center justify-between gap-x-3 border-b px-3 py-2 text-sm">
          <div className="flex items-center gap-2">
            <Download className="text-primary h-4 w-4" />
            <span className="font-medium">Downloading {title ? title : '...'}</span>
          </div>
          <span className="text-muted-foreground text-xs">{percent}%</span>
        </div>
        <div className="px-3 pb-2">
          <Progress
            value={percent}
            className="[&>div]:bg-brand-blue h-2 bg-gray-200 [&>div]:rounded-lg"
          />
        </div>
      </div>
    );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && isLoading) return;
        closeModal();
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        aria-describedby={undefined}
        showCloseButton={false}
      >
        <DialogHeader className="flex flex-row items-start justify-between">
          <DialogTitle>
            {mode === 'confirm' && 'Confirm CSV Download'}
            {mode === 'progress' && 'Downloading CSV Files'}
            {mode === 'complete' && 'Download Complete'}
          </DialogTitle>

          {mode === 'progress' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              aria-label="Minimize"
              onClick={() => setIsMinimized(true)}
            >
              <SquareMinus className="h-16 w-16" />
            </Button>
          )}
        </DialogHeader>

        {mode === 'confirm' && (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-3">
              <div className="flex justify-between text-sm text-gray-700">
                <p>Date:</p>
                <p className="font-medium">{date ? format(date, 'dd/MM/yyyy') : '-'}</p>
              </div>
              <div className="flex justify-between text-sm text-gray-700">
                <p>Total rows:</p>
                <p className="font-medium">{totalData.toLocaleString()}</p>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
              <p className="text-sm text-yellow-800">
                This dataset will be split into <strong>{totalBatchCalc}</strong> batches. Each file
                contains up to <strong>{limitPerFile.toLocaleString()}</strong> rows.
              </p>
            </div>
          </div>
        )}

        {mode === 'progress' && (
          <div className="flex flex-col gap-3">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>
                {progress} / {totalBatches} batches
              </span>
            </div>
            <Progress
              value={percent}
              className="[&>div]:bg-brand-blue h-3 bg-gray-200 [&>div]:rounded-full"
            />
            <div className="mt-4 flex flex-col items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-700">
              <p>
                Processing batch <strong>{progress + 1}</strong> of {totalBatches}...
              </p>
              <p className="mt-1 text-xs">
                Each batch will be automatically downloaded when ready.
              </p>
            </div>
          </div>
        )}

        {mode === 'complete' && (
          <div className="mt-4 flex flex-col items-center gap-3">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
            <p className="font-medium text-green-700">
              All {totalBatches} batches downloaded successfully!
            </p>
          </div>
        )}

        <DialogFooter className="mt-4">
          {mode === 'confirm' && (
            <>
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button onClick={onConfirm}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Start Download
              </Button>
            </>
          )}

          {mode === 'progress' && (
            <>
              {showCancelConfirm ? (
                <div className="flex w-full flex-col gap-2">
                  <Button variant="destructive" onClick={onCancel} className="w-full">
                    Yes, cancel download
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowCancelConfirm(false)}
                    className="w-full"
                  >
                    No, continue
                  </Button>
                </div>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={!isLoading}
                  className="w-full"
                >
                  Cancel
                </Button>
              )}
            </>
          )}

          {mode === 'complete' && (
            <Button onClick={closeModal} className="w-full">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
